import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FolderOpen, Plus } from "lucide-react";
import "@/styles/genie-banner.css";
import { useAuth } from "@/auth/AuthProvider";
import PageHeader from "@/components/common/PageHeader";
import { AddKnowledgeSheet, GenieViewTab, KnowledgeGeniusCard } from "@/components/knowledge-base";
import { Button } from "@/components/ui/button";
import LearnMoreButton from "@/components/learn/LearnMoreButton";
import { useAgentsList } from "@/hooks/agent";
import { usePaginatedDocuments } from "@/hooks/documents/useDocumentFetch";
import { resolveKnowledgeBaseRouteIntent } from "@/lib/knowledgeBaseAcquisition";
import type { UploadMethod } from "@/types/knowledge/knowledge-acquisition";

/** Page size for the header count query — we only read the totals, not the rows. */
const COUNT_ONLY_PAGE_SIZE = 1;

const KnowledgeBasePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadMethod, setUploadMethod] = useState<UploadMethod>("pdf");
  const [uploadTitle, setUploadTitle] = useState<string | undefined>(undefined);
  const [uploadSessionKey, setUploadSessionKey] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: allAgents = [] } = useAgentsList();
  const { data: documentPages } = usePaginatedDocuments(
    "my-documents",
    COUNT_ONLY_PAGE_SIZE,
    {},
    user?.id,
  );
  const connectedDocuments = documentPages?.pages[0]?.inKBCount ?? 0;

  useEffect(() => {
    const intent = resolveKnowledgeBaseRouteIntent(searchParams);
    if (!intent.uploadMethod) return;

    setUploadMethod(intent.uploadMethod);
    setUploadTitle(undefined);
    setUploadSessionKey((current) => current + 1);
    setUploadOpen(true);

    const nextParams = new URLSearchParams(searchParams);
    if (intent.shouldCleanAcquireParam) nextParams.delete("acquire");
    if (intent.shouldCleanLegacyTabParam) nextParams.delete("tab");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const openUpload = useCallback((method: UploadMethod = "pdf", title?: string) => {
    setUploadMethod(method);
    setUploadTitle(title);
    setUploadSessionKey((current) => current + 1);
    setUploadOpen(true);
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#F9FAFC]">
      <div className="container mx-auto max-w-[1920px] px-4 py-8">
        <PageHeader
          title="Knowledge Base"
          genieIcon="brain"
          subtitle="Choose a Genie to explore and manage the documents that shape its answers."
          subtitleMeta={<LearnMoreButton topic="knowledge-base" variant="link" />}
          action={
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Button
                onClick={() => openUpload("pdf")}
                className="h-10 gap-2 bg-gradient-to-r from-genie-primary to-genie-secondary font-semibold text-white shadow-md shadow-genie-primary/20 hover:from-genie-primary-hover hover:to-genie-secondary"
              >
                <Plus className="h-4 w-4" />
                Add knowledge
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/genie-hub/knowledge-base/documents")}
                className="h-10 gap-2 border-genie-primary/30 bg-white font-semibold text-genie-primary hover:bg-genie-primary/5"
              >
                <FolderOpen className="h-4 w-4" />
                All documents
                {connectedDocuments > 0 && (
                  <span className="rounded-full bg-genie-primary/10 px-2 py-0.5 text-xs font-semibold tabular-nums">
                    {connectedDocuments}
                  </span>
                )}
              </Button>
            </div>
          }
        />

        {/* Workspace-wide Knowledge Genius view: every watched source, every Genie. */}
        <KnowledgeGeniusCard />

        <GenieViewTab />
      </div>

      <AddKnowledgeSheet
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        sessionKey={uploadSessionKey}
        initialMethod={uploadMethod}
        initialTitle={uploadTitle}
        availableAgents={allAgents}
      />
    </div>
  );
};

export default KnowledgeBasePage;
